import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../lib/utils";
import { useEffect, useRef } from "react";

// Enhanced hook to robustly clean up pointer-events (same as Dialog)
function usePointerEventsCleanup(open?: boolean) {
    const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        // Setup MutationObserver to watch for pointer-events changes
        const observer = new MutationObserver(() => {
            // Check if dialog is closed but pointer-events is still set
            const dialogs = document.querySelectorAll('[role="dialog"]');
            const hasOpenDialogs = Array.from(dialogs).some(
                (dialog) => dialog.getAttribute('data-state') === 'open'
            );

            if (!hasOpenDialogs && document.body.style.pointerEvents === 'none') {
                if (process.env.NODE_ENV === 'development') console.log('[Sheet] Cleaning up stale pointer-events: none');
                document.body.style.pointerEvents = '';
            }
        });

        // Observe changes to body style attribute
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['style']
        });

        observerRef.current = observer;

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        // When dialog closes (open becomes false), ensure cleanup after animation
        if (open === false) {
            // Clear any existing timeout
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }

            // Set timeout to clean up after dialog close animation (500ms for Sheet)
            cleanupTimeoutRef.current = setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[Sheet] Cleaning up pointer-events after sheet close');
                    document.body.style.pointerEvents = '';
                }
            }, 550);
        }

        return () => {
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }
        };
    }, [open]);

    // Final cleanup on unmount
    useEffect(() => {
        return () => {
            // Use setTimeout to ensure this runs after Radix cleanup
            setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[Sheet] Final cleanup on unmount');
                    document.body.style.pointerEvents = '';
                }
            }, 100);
        };
    }, []);
}

// Wrapper component for Sheet.Root that adds pointer-events cleanup
const SheetRoot: React.FC<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>> = (props) => {
    usePointerEventsCleanup(props.open);
    return <SheetPrimitive.Root {...props} />;
};

const Sheet = SheetRoot;
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close
const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        className={cn(
            "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className
        )}
        {...props}
        ref={ref}
    />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
    "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
    {
        variants: {
            side: {
                top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
                bottom:
                    "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
                left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
                right:
                    "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
            },
        },
        defaultVariants: {
            side: "right",
        },
    }
)

const containedSheetVariants = cva(
    "flex h-full z-30 gap-4 bg-background absolute shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
    {
        variants: {
            side: {
                top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
                bottom:
                    "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
                left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left ",
                right:
                    "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            },
        },
        defaultVariants: {
            side: "right",
        },
    }
)

interface SheetContentProps
    extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
        VariantProps<typeof sheetVariants> {
    contained?: boolean
}

const SheetContent = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Content>,
    SheetContentProps
>(({ side = "right", className, children, contained = false, ...props }, ref) => {
    const handleInteractOutside = React.useCallback((event: any) => {
        if (contained) {
            event.preventDefault();
        }
    }, [contained]);

    const content = (
        <SheetPrimitive.Content
            ref={ref}
            className={cn(
                contained
                    ? containedSheetVariants({ side })
                    : sheetVariants({ side }),
                className,
                contained ? "h-full p-0" : ""
            )}
            onInteractOutside={handleInteractOutside}
            {...props}
        >
            {children}
            <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
        </SheetPrimitive.Content>
    );

    return contained ? content : (
        <SheetPortal>
            <SheetOverlay />
            {content}
        </SheetPortal>
    );
});
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
                         className,
                         ...props
                     }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col space-y-2 text-center sm:text-left",
            className
        )}
        {...props}
    />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
                         className,
                         ...props
                     }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className
        )}
        {...props}
    />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        className={cn("text-lg font-semibold text-foreground", className)}
        {...props}
    />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
    Sheet,
    SheetPortal,
    SheetOverlay,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
}